import React, {
  useEffect,
  useState,
} from 'react'
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap'
import {
  useCurrentUserQuery,
  useUpdateProfileMutation,
} from '@data/currentUser/currentUser'

import './UserInfo.scss'

interface IUserInfoModalProps {
  show: boolean
  onHide: () => void
}

const UserInfo: React.FC<IUserInfoModalProps> = ({ show, onHide }: IUserInfoModalProps) => {
  const { data: userInfo } = useCurrentUserQuery()
  const updateProfileMutation = useUpdateProfileMutation()
  const resetProfileMutation = updateProfileMutation.reset

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [mobile, setMobile] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
  })

  const trimmedFirstName = firstName.trim()
  const trimmedLastName = lastName.trim()
  const didProfileInfoChange = !!userInfo && (
    userInfo.firstname !== firstName ||
    userInfo.lastname !== lastName ||
    (userInfo.mobile || '') !== mobile
  )
  const showFirstNameError = !trimmedFirstName && (submitted || touchedFields.firstName)
  const showLastNameError = !trimmedLastName && (submitted || touchedFields.lastName)

  useEffect(() => {
    // Keep the modal seeded from the latest server state while pristine,
    // but do not overwrite in-progress edits during background refetches.
    if (show && userInfo && !didProfileInfoChange && !updateProfileMutation.isPending) {
      setFirstName(userInfo.firstname)
      setLastName(userInfo.lastname)
      setMobile(userInfo.mobile ?? '')
    }
  }, [show, userInfo, didProfileInfoChange, updateProfileMutation.isPending])

  useEffect(() => {
    if (!show) {
      setSubmitted(false)
      setTouchedFields({
        firstName: false,
        lastName: false,
      })
      if (userInfo) {
        setFirstName(userInfo.firstname)
        setLastName(userInfo.lastname)
        setMobile(userInfo.mobile ?? '')
      }
      resetProfileMutation()
    }
  }, [show, userInfo, resetProfileMutation])

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setSubmitted(true)

    if (userInfo && didProfileInfoChange && trimmedFirstName && trimmedLastName) {
      updateProfileMutation.mutate({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        mobile,
      })
    }
  }

  return (
    <Modal
      onHide={onHide}
      show={show}
      backdrop="static"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header className="" closeButton>
        <Modal.Title id="contained-modal-title-vcenter">Update Personal Information</Modal.Title>
      </Modal.Header>
      <Modal.Body className="custom-modal-body">
        <Form className="device-information-form" onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="form-user-email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                readOnly
                autoComplete="off"
                type="text"
                placeholder="Email"
                value={userInfo?.email}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="form-user-firsname">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                autoComplete="off"
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
                onBlur={() => setTouchedFields((current) => ({ ...current, firstName: true }))}
                isInvalid={showFirstNameError}
                disabled={updateProfileMutation.isPending}
              />
              <Form.Control.Feedback type="invalid">
                <div className="input-feedback_error">Required</div>
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="form-user-lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                autoComplete="off"
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
                onBlur={() => setTouchedFields((current) => ({ ...current, lastName: true }))}
                isInvalid={showLastNameError}
                disabled={updateProfileMutation.isPending}
              />
              <Form.Control.Feedback type="invalid">
                <div className="input-feedback_error">Required</div>
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="form-user-mobile">
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                autoComplete="off"
                type="text"
                placeholder="Mobile"
                value={mobile}
                onChange={(e) => setMobile(e.currentTarget.value)}
                disabled={updateProfileMutation.isPending}
              />
            </Form.Group>
          </Row>
          <Button
            variant="primary"
            type="submit"
            disabled={
              !trimmedFirstName ||
              !trimmedLastName ||
              updateProfileMutation.isPending ||
              !didProfileInfoChange
            }
          >
            {updateProfileMutation.isPending ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span>Updating profile...</span>
              </>
            ) : (
              <>Update Profile</>
            )}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer className="custom-modal-footer">
        <Button id="close" variant="secondary" disabled={updateProfileMutation.isPending} onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default UserInfo
